import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  requirement_type: string;
  requirement_value: number;
  requirement_data: any;
  points: number;
  rarity: string;
  is_hidden: boolean;
  sort_order: number;
}

export interface AchievementProgress {
  id: string;
  achievement_id: string;
  current_progress: number;
  completed: boolean;
  completed_at: string | null;
  achievement?: Achievement;
}

export interface AchievementSummary {
  total_achievements: number;
  earned_achievements: number;
  total_points: number;
  completion_percentage: number;
}

export function useAchievements() {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userProgress, setUserProgress] = useState<AchievementProgress[]>([]);
  const [summary, setSummary] = useState<AchievementSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch all achievements
  const fetchAchievements = useCallback(async () => {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching achievements:', error);
      return;
    }

    setAchievements(data || []);
  }, []);

  // Fetch user's achievement progress
  const fetchUserProgress = useCallback(async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('user_achievement_progress')
      .select(`
        *,
        achievement:achievements(*)
      `)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching user progress:', error);
      return;
    }

    setUserProgress(data || []);
  }, [user?.id]);

  // Fetch achievement summary
  const fetchSummary = useCallback(async () => {
    if (!user?.id) return;

    const { data, error } = await supabase.rpc('get_user_achievement_summary', {
      p_user_id: user.id
    });

    if (error) {
      console.error('Error fetching summary:', error);
      return;
    }

    if (data && data.length > 0) {
      setSummary(data[0]);
    }
  }, [user?.id]);

  // Check and update achievements based on current stats
  const checkGameAchievements = useCallback(async () => {
    if (!user?.id) return;

    const { error } = await supabase.rpc('check_game_achievements', {
      p_user_id: user.id
    });

    if (error) {
      console.error('Error checking achievements:', error);
    } else {
      // Refresh progress after checking
      await fetchUserProgress();
      await fetchSummary();
    }
  }, [user?.id, fetchUserProgress, fetchSummary]);

  // Update achievement progress manually
  const updateProgress = useCallback(async (
    achievementId: string,
    progressIncrement: number = 1
  ) => {
    if (!user?.id) return;

    const { error } = await supabase.rpc('update_achievement_progress', {
      p_user_id: user.id,
      p_achievement_id: achievementId,
      p_progress_increment: progressIncrement
    });

    if (error) {
      console.error('Error updating progress:', error);
    } else {
      await fetchUserProgress();
      await fetchSummary();
    }
  }, [user?.id, fetchUserProgress, fetchSummary]);

  // Get progress for a specific achievement
  const getProgress = useCallback((achievementId: string): AchievementProgress | null => {
    return userProgress.find(p => p.achievement_id === achievementId) || null;
  }, [userProgress]);

  // Get progress percentage
  const getProgressPercentage = useCallback((achievementId: string): number => {
    const achievement = achievements.find(a => a.id === achievementId);
    const progress = getProgress(achievementId);
    
    if (!achievement || !progress) return 0;
    
    return Math.min(100, (progress.current_progress / achievement.requirement_value) * 100);
  }, [achievements, getProgress]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('achievement-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_achievement_progress',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Achievement update:', payload);
          
          // Show notification for newly completed achievements
          if (payload.eventType === 'UPDATE' && payload.new.completed && !payload.old?.completed) {
            const achievement = achievements.find(a => a.id === payload.new.achievement_id);
            if (achievement) {
              toast({
                title: '🏆 Achievement Unlocked!',
                description: `${achievement.icon} ${achievement.name} - ${achievement.description}`,
                duration: 5000,
              });
            }
          }
          
          fetchUserProgress();
          fetchSummary();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, achievements, fetchUserProgress, fetchSummary]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAchievements();
      if (user?.id) {
        await Promise.all([
          fetchUserProgress(),
          fetchSummary()
        ]);
      }
      setLoading(false);
    };

    loadData();
  }, [user?.id, fetchAchievements, fetchUserProgress, fetchSummary]);

  return {
    achievements,
    userProgress,
    summary,
    loading,
    checkGameAchievements,
    updateProgress,
    getProgress,
    getProgressPercentage,
    refreshData: useCallback(async () => {
      await Promise.all([
        fetchAchievements(),
        fetchUserProgress(),
        fetchSummary()
      ]);
    }, [fetchAchievements, fetchUserProgress, fetchSummary])
  };
}
